import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet';
import { ActivityItem } from '../BillingActivityTabContent/column';
import { useQuery } from '@apollo/client';
import {
  BillingItemType,
  CommissionStatus,
  OrderShopifyProduct,
} from 'app/graphql/generated/admin/graphql';
import { ADMIN_ORDER_BY_ID } from 'app/graphql';
import { Section, SectionContainer, SectionContent, SectionHeader } from './style';
import { CardTitle } from 'components/ui/card';
import { OrderBreakdownCard } from '../OrderBreakdownCard';
import { useMemo } from 'react';
import { CommissionCard } from '../CommissionCard';
import { ScrollArea, ScrollBar } from 'components/ui/scrolll-area';
import { constantCase } from 'change-case';
import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import { VolumeTierCard } from '../VolumeTierCard';
import { ProductsList } from '../ProductsList';
import { marketplaceEnumToString } from '../../../../utils/marketplaceSelector';
import { GMVTierCard } from '../GMVTierCard';

type Props = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  item?: ActivityItem;
};

export const ActivityDetailSheet = ({ open, onOpenChange, item }: Props) => {
  const user = useAppSelector(selectUserInfo);
  const { data } = useQuery(ADMIN_ORDER_BY_ID, {
    skip: !item,
    context: {
      clientName: 'admin',
    },
    variables: {
      id: item?.orderId.toString() ?? '',
    },
  });

  const order = data?.orderById;

  const products = useMemo(() => {
    return (
      order?.products.map((product) => {
        if (product.__typename === 'OrderShopifyProduct') {
          return {
            productId: product.productId,
            variantId: product.variantId,
            quantity: product.quantity,
            title: product.title,
            storeCanonicalURL: product.storeCanonicalURL,
          } satisfies OrderShopifyProduct;
        }
        return product;
      }) ?? []
    );
  }, [order]);

  if (!order || !item) {
    return null;
  }

  const transformedType = constantCase(item.type);

  const isCommission =
    transformedType === BillingItemType.AffiliateCommission ||
    transformedType === BillingItemType.MarkupCommission ||
    transformedType === BillingItemType.MerchantCommission ||
    transformedType === BillingItemType.DiscountCommission ||
    transformedType === BillingItemType.AffiliateCommissionDeveloperShare ||
    transformedType === BillingItemType.MarkupCommissionDeveloperShare ||
    transformedType === BillingItemType.MerchantCommissionDeveloperShare ||
    transformedType === BillingItemType.DiscountCommissionDeveloperShare ||
    transformedType === BillingItemType.AffiliateCommissionRyeShare ||
    transformedType === BillingItemType.MarkupCommissionRyeShare ||
    transformedType === BillingItemType.MerchantCommissionRyeShare ||
    transformedType === BillingItemType.DiscountCommissionRyeShare;

  const renderCommissionSection = () => {
    let rate = order.commission?.rate;
    let status = order.commission?.status ?? CommissionStatus.Pending;

    const subtotalCents = order.breakdown?.subtotalCents;
    const marginCents = order.breakdown?.marginCents;

    // Determine if item is for shares other than Markup commissions
    const isDeveloperShares =
      transformedType == BillingItemType.DiscountCommissionDeveloperShare ||
      transformedType == BillingItemType.AffiliateCommissionDeveloperShare ||
      transformedType == BillingItemType.MarkupCommissionDeveloperShare ||
      transformedType == BillingItemType.MerchantCommissionDeveloperShare;

    const isRyeShare =
      transformedType == BillingItemType.DiscountCommissionRyeShare ||
      transformedType == BillingItemType.AffiliateCommissionRyeShare ||
      transformedType == BillingItemType.MarkupCommissionRyeShare ||
      transformedType == BillingItemType.MerchantCommissionRyeShare;

    if (!subtotalCents) {
      return <></>;
    }

    // Calculate Markup commission breakdown
    if (
      transformedType === BillingItemType.MarkupCommission &&
      user?.isApprovedForDirectCheckout &&
      item.receiver === 'Developer' &&
      marginCents
    ) {
      rate = Math.round(100 * (marginCents / subtotalCents));
    } else if (transformedType === BillingItemType.AffiliateCommission) {
      rate = Math.round(
        ((Number(item.amount.replaceAll(' USD', '').replaceAll('$', '')) * 100) / subtotalCents) *
          100,
      );
    } else if (transformedType == BillingItemType.MerchantCommission) {
      rate = item.billingSetting.merchantCommissionRate;
    } else if (isDeveloperShares) {
      rate = item.billingSetting.developerCommissionShare;
    } else if (isRyeShare) {
      rate = 100 - item.billingSetting.developerCommissionShare;
    }

    if (transformedType === BillingItemType.DiscountCommission) {
      status = CommissionStatus.Discounted;
    } else if (transformedType === BillingItemType.MarkupCommission) {
      status = CommissionStatus.Sent;
    } else if (transformedType === BillingItemType.MarkupCommissionDeveloperShare) {
      status = CommissionStatus.Sent;
    }

    if (!rate || !isCommission) {
      console.log('returning empty');
      return <></>;
    }

    return (
      <Section>
        <SectionHeader>
          <SheetTitle>
            Commission details{' '}
            <span className="text-muted-foreground">({order.breakdown?.currency})</span>
          </SheetTitle>
        </SectionHeader>
        <SectionContent>
          <CommissionCard
            rate={rate}
            type={item.type}
            receiver={item.receiver}
            status={status ?? CommissionStatus.Pending}
            amount={item.amount.split(' ')[0]}
            isRefund={order.breakdown?.totalCents === 0}
          />
        </SectionContent>
      </Section>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-6/12 overflow-y-auto">
        <SheetHeader className="space-y-2">
          <CardTitle>Billing Item #{item.id}</CardTitle>
          <SheetDescription>Order Id • {item.orderId}</SheetDescription>
        </SheetHeader>

        <SectionContainer>
          {order.breakdown && (
            <Section>
              <SectionHeader>
                <SheetTitle>
                  Order Breakdown{' '}
                  <span className="text-muted-foreground">({order.breakdown?.currency})</span>
                </SheetTitle>
              </SectionHeader>
              <SectionContent>
                <OrderBreakdownCard breakdown={order.breakdown} />
              </SectionContent>
            </Section>
          )}

          {renderCommissionSection()}
          {item.volumeTiers?.length &&
            (item.typeEnum === BillingItemType.PlatformFee ||
              item.typeEnum === BillingItemType.PlatformFeeRefund) && (
              <Section>
                <SectionHeader>
                  <SheetTitle>Volume tier</SheetTitle>
                </SectionHeader>
                <SectionContent>
                  <VolumeTierCard
                    volumeTiers={item.volumeTiers}
                    amount={item.amountCents}
                    type={item.typeEnum}
                  />
                </SectionContent>
              </Section>
            )}
          {item.gmvTier && item.typeEnum === BillingItemType.RyeServiceFee && (
            <Section>
              <SectionHeader>
                <SheetTitle>GMV Breakdown</SheetTitle>
              </SectionHeader>
              <SectionContent>
                <GMVTierCard gmvTier={item.gmvTier} amount={item.amountCents} />
              </SectionContent>
            </Section>
          )}

          <Section>
            <SectionHeader>
              <SheetTitle>Products</SheetTitle>
            </SectionHeader>
            <SectionContent>
              <ScrollArea className="mb-[24px] whitespace-nowrap rounded-md border">
                <ProductsList
                  products={products}
                  marketplace={marketplaceEnumToString(order.marketplace)}
                />
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </SectionContent>
          </Section>
        </SectionContainer>
      </SheetContent>
    </Sheet>
  );
};
